import React from 'react';
import PageHeader from '@/components/shared/PageHeader';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  getAccountingPrintSettings,
  saveAccountingPrintSettings,
  type AccountingPrintSettings,
} from '@/lib/printSettings';

const AccountingSettingsPage: React.FC = () => {
  const [settings, setSettings] = React.useState<AccountingPrintSettings>(() => getAccountingPrintSettings());
  const [saved, setSaved] = React.useState(false);

  const onLogoChange: React.ChangeEventHandler<HTMLInputElement> = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      setSettings((prev) => ({ ...prev, logoDataUrl: String(reader.result || '') }));
    };
    reader.readAsDataURL(file);
  };

  const save = () => {
    saveAccountingPrintSettings(settings);
    setSaved(true);
    setTimeout(() => setSaved(false), 2000);
  };

  return (
    <div className="w-full min-w-0 space-y-6">
      <PageHeader
        title="تنظیمات چاپ حسابداری"
        description="تنظیم لوگو، QR، واترمارک و اطلاعات خروجی PDF"
      />

      <Card>
        <CardHeader>
          <CardTitle className="text-base">تنظیمات اصلی</CardTitle>
        </CardHeader>
        <CardContent className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <div>
            <label className="mb-1 block text-xs text-slate-600">نام شرکت</label>
            <Input
              value={settings.companyName}
              onChange={(e) => setSettings((p) => ({ ...p, companyName: e.target.value }))}
            />
          </div>
          <div>
            <label className="mb-1 block text-xs text-slate-600">متن QR</label>
            <Input
              value={settings.qrText}
              onChange={(e) => setSettings((p) => ({ ...p, qrText: e.target.value }))}
            />
          </div>
          <div>
            <label className="mb-1 block text-xs text-slate-600">متن واترمارک</label>
            <Input
              value={settings.watermarkText}
              onChange={(e) => setSettings((p) => ({ ...p, watermarkText: e.target.value }))}
            />
          </div>
          <div>
            <label className="mb-1 block text-xs text-slate-600">جهت چاپ</label>
            <select
              value={settings.pageOrientation}
              onChange={(e) =>
                setSettings((p) => ({
                  ...p,
                  pageOrientation: e.target.value === 'landscape' ? 'landscape' : 'portrait',
                }))
              }
              className="h-10 w-full rounded-md border border-slate-300 bg-white px-3 text-sm outline-none focus:border-slate-400"
            >
              <option value="portrait">عمودی (Portrait)</option>
              <option value="landscape">افقی (Landscape)</option>
            </select>
          </div>
          <div>
            <label className="mb-1 block text-xs text-slate-600">لوگو</label>
            <Input type="file" accept="image/*" onChange={onLogoChange} />
            {settings.logoDataUrl ? (
              <img src={settings.logoDataUrl} alt="logo" className="mt-2 h-12 w-12 rounded border object-contain" />
            ) : null}
          </div>
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={settings.showQr}
              onChange={(e) => setSettings((p) => ({ ...p, showQr: e.target.checked }))}
            />
            نمایش QR در چاپ
          </label>
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={settings.watermarkEnabled}
              onChange={(e) => setSettings((p) => ({ ...p, watermarkEnabled: e.target.checked }))}
            />
            فعال بودن واترمارک
          </label>
        </CardContent>
      </Card>

      <div className="flex justify-end">
        <Button type="button" onClick={save}>
          ذخیره تنظیمات
        </Button>
      </div>
      {saved ? <div className="text-sm text-emerald-700">تنظیمات ذخیره شد.</div> : null}
    </div>
  );
};

export default AccountingSettingsPage;
